import { PageLayout } from '@/layouts/page-layout'
import { GroupInput } from '@/components/v2/group-input'
import { useTranslation } from 'react-i18next'
import { Button, Input, Typography } from '@material-tailwind/react'
import { Link, useNavigation } from 'react-router-dom'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { ContractSearcher } from '@/components/contract-searcher'
import { ContractSearcherContext } from '@/components/contract-searcher/context'
import { list } from '@/router/utils'
import { useAuthorization } from '@/hooks'
import { HasAccess } from '@permify/react-role'
import { NotificationModal } from '@/components/notification-modal'
import { useCreateAssetMutation } from '@/store/services'

export const AssetCreate = () => {
  const { state } = useNavigation()
  const [createAsset, { isLoading }] = useCreateAssetMutation()
  const { hasAccess } = useAuthorization({
    redirection: {
      enable: true
    }
  })
  
  // Estados para notificaciones
  const [showNotification, setShowNotification] = useState(false)
  const [notificationIcon, setNotificationIcon] = useState('')
  const [notificationTitle, setNotificationTitle] = useState('')
  const [notificationMessage, setNotificationMessage] = useState('')

  const initialValues = {
    contractor_contract_id: null,
    accreditation_date: '',
    expiration_date: '',
    status: 'active', // Valor por defecto necesario
    vehicle_info: {
      type: '',
      patent: '',
      brand: '',
      model: '',
      year: '',
      color: ''
    }
  }

  const { t } = useTranslation()

  useEffect(() => {
    hasAccess('asset#create')
  }, [])

  const handleSubmit = async (values) => {
    try {
      await createAsset(values).unwrap();
      
      setNotificationIcon('check');
      setNotificationTitle(t('asset.notifications.success', { 
        what: t('asset.one') 
      }));
      setNotificationMessage(t('asset.notifications.create.success'));
      setShowNotification(true);
    } catch (error) {
      const errors = error.data?.errors || [];
      
      // Usar bullet points Unicode (•) con saltos de línea
      const bulletList = errors.map(e => `• ${e}`).join('\n');
      const formattedMessage = errors.length > 1 
        ? `${t('errors.following_issues')}\n${bulletList}`
        : errors[0] || t('errors.generic');
  
      setNotificationIcon('error');
      setNotificationTitle(t('asset.notifications.error', { 
        what: t('asset.one') 
      }));
      setNotificationMessage(formattedMessage);
      setShowNotification(true);
    }
  };

  return (
    <PageLayout
      title={t('actions.create.with', { what: t('asset.one').toLowerCase() })}
    >
      {showNotification && (
        <NotificationModal
          iconType={notificationIcon}
          title={notificationTitle}
          message={notificationMessage}
          returnPath={notificationIcon === 'check' ? list('assets') : undefined}
          setBackStateFunction={() => setShowNotification(false)}
        />
      )}

      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ handleChange, values, setFieldValue }) => (
          <Form>
            <GroupInput
              title={t('common.overall')}
              description='Rellena la información general del nuevo activo.'
              className='py-8'
              divider={false}
            >
              <div className='grid grid-cols-2 gap-4'>
                <ContractSearcherContext.Provider value={{ currentContract: {} }}>
                  {/* Campo para contractor_contract_id */}
                  <ContractSearcher 
                    required 
                    name="contractor_contract_id"
                    onChange={(selected) => setFieldValue('contractor_contract_id', selected?.id)}
                  />
                </ContractSearcherContext.Provider>
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <Input
                  name="accreditation_date"
                  label={t('common.accreditationDate')}
                  type='date'
                  value={values.accreditation_date}
                  onChange={handleChange}
                />
                <Input
                  name="expiration_date"
                  label={t('common.expirationDate')}
                  type='date'
                  value={values.expiration_date}
                  onChange={handleChange}
                />
              </div>

              <section className='bg-gray-100 px-8 py-6 rounded-2xl grid grid-rows-1 gap-4'>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='col-span-2'>
                    <Typography variant='h6' className='text-lg'>
                      Datos vehículo
                    </Typography>
                    <Typography variant='small' className='text-gray-500'>
                      Rellena los siguientes campos con la información del vehículo.
                    </Typography>
                  </div>

                  <Input
                    required
                    name="vehicle_info.type"
                    value={values.vehicle_info.type}
                    onChange={handleChange}
                    label='Tipo de activo'
                  />
                  <Input
                    name="vehicle_info.patent"
                    value={values.vehicle_info.patent}
                    onChange={handleChange}
                    label='Patente'
                  />
                  <Input
                    required
                    name="vehicle_info.brand"
                    value={values.vehicle_info.brand}
                    onChange={handleChange}
                    label='Marca'
                  />
                  <Input
                    required
                    name="vehicle_info.model"
                    value={values.vehicle_info.model}
                    onChange={handleChange}
                    label='Modelo'
                  />
                  <Input
                    name="vehicle_info.year"
                    value={values.vehicle_info.year}
                    onChange={handleChange}
                    label='Año'
                  />
                  <Input
                    name="vehicle_info.color"
                    value={values.vehicle_info.color}
                    onChange={handleChange}
                    label='Color'
                  />
                </div>
              </section>
            </GroupInput>

            <div className='flex justify-end gap-3 mt-10'>
              <Link to={list('assets')}>
                <Button variant='text'>
                  {t('actions.cancel')}
                </Button>
              </Link>
              <HasAccess permissions='asset#create' >
                <Button type='submit' loading={isLoading}>
                  {t('actions.create.one')}
                </Button>
              </HasAccess>
            </div>
          </Form>
        )}
      </Formik>
    </PageLayout>
  )
}